import * as React from 'react';
import { LIMIT } from 'Src/consts/table';
import { Tournament } from 'Src/models/tournament';
import {
  PageHelpDefinition,
  PageHelpFilter,
  PageHelpRegistration,
  registerPageHelpDefinition
} from 'Src/helpers/pageHelp/pageHelp';

type ListKind = 'upcoming' | 'past';

interface Props {
  kind: ListKind;
  isLoading: boolean;
  total: number;
  currentPage: number;
  filters: any;
  selectedItems: Tournament[];
  onAddTournament: () => void;
  onOpenFilters: () => void;
  onClearFilters: () => void;
  onManageTournament: (tournament: Tournament) => void;
}

const FILTER_LABELS = {
  name: 'Tournament',
  season: 'Season',
  sportId: 'Sport',
  startTime: 'Start date',
  endTime: 'End date',
  isPublishOnTheWebsite: 'Published',
  isPromoteTournament: 'Highlighted',
  tournamentTag: 'Tag'
};

const hasValue = (value: any): boolean => {
  if (Array.isArray(value)) return value.length > 0;
  if (value && typeof value === 'object') return Object.keys(value).some(key => hasValue(value[key]));
  return value !== '' && value !== null && typeof value !== 'undefined';
};

const describeValue = (value: any): string => {
  if (Array.isArray(value)) return value.length === 1 ? String(value[0]) : `${value.length} selected`;
  if (value && typeof value === 'object') {
    const values = Object.keys(value)
      .map(key => value[key])
      .filter(hasValue);
    if (values.length === 2) return 'Date range';
    return values.length ? String(values[0]) : '';
  }
  return String(value);
};

const getFilters = (filters: any): PageHelpFilter[] =>
  Object.keys(filters || {})
    .filter(key => hasValue(filters[key]))
    .map(key => ({
      id: key,
      label: FILTER_LABELS[key] || key,
      value: describeValue(filters[key])
    }));

export const UnionTournamentListPageHelp: React.FunctionComponent<Props> = props => {
  const registrationRef = React.useRef<PageHelpRegistration | null>(null);
  const isPast = props.kind === 'past';
  const selectedCount = props.selectedItems.length;
  const selectedTournament = selectedCount === 1 ? props.selectedItems[0] : undefined;
  const filters = getFilters(props.filters);
  const totalPages = Math.max(1, Math.ceil(props.total / LIMIT));
  const filterSignature = JSON.stringify(props.filters || {});
  const selectionSignature = props.selectedItems.map(item => item.id).join('|');

  const definition: PageHelpDefinition = {
    screenId: isPast ? 'union.tournaments.past' : 'union.tournaments.upcoming',
    title: isPast ? 'Past tournaments' : 'Upcoming tournaments',
    subtitle: isPast
      ? 'Find completed tournaments and continue managing their operational records.'
      : 'Create tournaments and keep upcoming entries, competition setup and publishing ready.',
    entity: { singular: 'tournament', plural: 'tournaments' },
    useDefaultSelectionObservation: false,
    state: {
      loading: props.isLoading,
      total: props.total,
      visibleCount: Math.min(LIMIT, props.total),
      selectedCount,
      currentPage: props.currentPage,
      totalPages,
      activeView: isPast ? 'Past' : 'Upcoming',
      filters
    },
    observations: [
      ...(isPast
        ? [
            {
              id: 'past-is-editable',
              tone: 'positive' as 'positive',
              title: 'Past tournaments remain fully manageable',
              text:
                'Completion does not lock the tournament. You can still correct schools, participants, teams, event allocations, fixtures, scores, placings and published results.'
            }
          ]
        : []),
      ...(selectedCount === 1 && selectedTournament
        ? [
            {
              id: 'one-selected',
              tone: 'neutral' as 'neutral',
              title: `${selectedTournament.name} selected`,
              text: isPast
                ? 'Open Manage tournament to continue editing this completed tournament. Bulk actions are separate from opening the record.'
                : 'Open Manage tournament for the full workflow. The selection actions can also update publishing, highlighting or tags.'
            }
          ]
        : selectedCount > 1
        ? [
            {
              id: 'many-selected',
              tone: 'warning' as 'warning',
              title: `${selectedCount} tournaments selected`,
              text:
                'Bulk actions affect every selected tournament. Review the selection carefully before changing publishing, tags or deleting records.'
            }
          ]
        : [])
    ],
    actions: [
      {
        id: 'union-tournaments-add',
        label: 'Add tournament',
        description: 'Start a new tournament and complete its core details before configuring entries and competition.',
        kind: isPast ? 'secondary' : 'primary',
        anchorId: 'union-tournaments-add',
        run: props.onAddTournament
      },
      {
        id: 'union-tournaments-open-filters',
        label: 'Open filters',
        description: 'Narrow the full server-side result set by season, dates, sport, publishing state or tags.',
        anchorId: 'union-tournaments-list',
        run: props.onOpenFilters
      },
      ...(filters.length
        ? [
            {
              id: 'union-tournaments-clear-filters',
              label: 'Clear all filters',
              description: 'Return to the complete tournament list for this tab.',
              run: props.onClearFilters
            }
          ]
        : []),
      ...(selectedTournament
        ? [
            {
              id: 'union-tournaments-manage-selected',
              label: isPast ? 'Manage past tournament' : 'Manage selected tournament',
              description: isPast
                ? 'Open every management area, including entrants, teams, events and results. Past records are not read-only.'
                : 'Open the tournament overview and all operational management areas.',
              kind: 'primary' as 'primary',
              anchorId: 'union-tournaments-list',
              run: () => props.onManageTournament(selectedTournament)
            }
          ]
        : [])
    ],
    anchors: [
      {
        id: 'union-tournaments-tabs',
        label: 'Tournament lists',
        description:
          'Upcoming contains future work, Past contains completed tournaments, and Invites contains invitations sent to schools.'
      },
      {
        id: 'union-tournaments-summary',
        label: 'Tournament summary',
        description:
          'Use these counters as shortcuts to upcoming work, the next start and unanswered confirmation requests.'
      },
      {
        id: 'union-tournaments-add',
        label: 'Add tournament',
        description: 'Create a new tournament. It can be configured in detail after the initial record is saved.'
      },
      {
        id: 'union-tournaments-list',
        label: isPast ? 'Past tournament table' : 'Upcoming tournament table',
        description: isPast
          ? 'Click a row to manage the completed tournament. Its entrants, teams, events, allocations and results remain editable.'
          : 'Click a row to open the full management hub. Use checkboxes only for selection and bulk actions.'
      },
      {
        id: 'union-tournaments-search',
        label: 'Search tournaments',
        description:
          'Enter part of a tournament name and press Enter. The search is applied to the complete list, not only this page.',
        selector: '.unionTournamentsListCard .gridModernSearch'
      },
      {
        id: 'union-tournaments-filters',
        label: 'Tournament filters',
        description:
          'Combine season, sport, date, publishing, highlighting and tag criteria. Apply closes the panel and refreshes the full result set.',
        selector: '.unionTournamentsListCard .gridModernFiltersAnchor'
      },
      {
        id: 'union-tournaments-columns',
        label: 'Table columns',
        description:
          'Choose which tournament details appear in this list. The tournament name stays available and preferences are saved for this user.',
        selector: '.unionTournamentsListCard .gridModernColumns'
      },
      {
        id: 'union-tournaments-pagination',
        label: 'Pagination',
        description:
          totalPages === 1
            ? 'All matching tournaments fit on this page.'
            : `There are ${totalPages} pages. Use Previous and Next to move through the complete result set.`,
        selector: '.unionTournamentsListCard .gridModernPagination'
      }
    ],
    sections: [
      {
        id: 'open-versus-select',
        title: 'Opening and selecting are different',
        items: [
          'Click anywhere on a tournament row to open its management hub.',
          'Use a checkbox when you want to run a selection action such as publishing, highlighting or assigning tags.',
          'Deletion remains a confirmed destructive action and is never run directly by Squady.'
        ],
        anchorIds: [
          'union-tournaments-list',
          'union-tournaments-search',
          'union-tournaments-filters',
          'union-tournaments-columns',
          'union-tournaments-pagination'
        ]
      },
      ...(isPast
        ? [
            {
              id: 'past-management',
              title: 'Past does not mean locked',
              items: [
                'Open a past tournament exactly as you would an upcoming one.',
                'You can add or correct participants and teams after the date has passed.',
                'Fixtures, event allocations, scores, placings and results can still be completed or corrected.',
                'Publishing and tournament settings remain available where your permissions allow them.'
              ],
              anchorIds: ['union-tournaments-list']
            }
          ]
        : [
            {
              id: 'upcoming-flow',
              title: 'Recommended setup order',
              items: [
                'Set entry rules and choose eligible schools.',
                'Send invitations, monitor participants and waiting-list places, then request final confirmations.',
                'Create teams, groups, pitches and fixtures or individual events before publishing the final programme.'
              ],
              anchorIds: ['union-tournaments-list']
            }
          ])
    ]
  };

  React.useEffect(() => {
    registrationRef.current = registerPageHelpDefinition(definition);
    return () => {
      if (registrationRef.current) registrationRef.current.unregister();
      registrationRef.current = null;
    };
  }, []);

  React.useEffect(() => {
    if (registrationRef.current) registrationRef.current.update(definition);
  }, [props.kind, props.isLoading, props.total, props.currentPage, filterSignature, selectionSignature]);

  return null;
};
